import { FC } from 'react'

import {
  BankingTransactionFilter,
  ReconciliationWidgetDocument,
  ReconciliationWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  displayedFilters: (string | string[])[]
}

interface QueryArgsType {
  filter?: BankingTransactionFilter
}

const prepareData = (data: ReconciliationWidgetQuery, prepareDataArgs: PreparedDataArgsType) => {
  const totalTransactions = data?.banking?.total?.aggregate?.count || 0
  const accountedTransactions = data?.banking?.accounted?.aggregate?.count || 0
  const reconciledTransactions = data?.banking?.reconciled?.aggregate?.count || 0
  const accountedpercentage =
    totalTransactions === 0 ? 0 : Math.floor((accountedTransactions * 100) / totalTransactions)
  const reconciledpercentage =
    accountedpercentage === 0
      ? 0
      : Math.floor((reconciledTransactions * 100) / accountedTransactions)

  const countersChartDatas = [
    {
      label: i18n.t('finance.overview.reconciliation.transactions_registered'),
      value: totalTransactions,
      formattedValue: totalTransactions.toString(),
    },
    {
      label: i18n.t('finance.overview.reconciliation.accounted'),
      value: accountedpercentage,
      formattedValue: `${accountedpercentage} %`,
    },
    {
      label: i18n.t('finance.overview.reconciliation.reconciled'),
      value: reconciledpercentage,
      formattedValue: `${reconciledpercentage} %`,
    },
  ]

  const chartDatas = [
    {
      label: i18n.t('finance.overview.reconciliation.transactions_registered'),
      value: { completed: totalTransactions, pending: 0 },
    },
    {
      label: i18n.t('finance.overview.reconciliation.accounted'),
      value: {
        completed: accountedTransactions,
        pending: totalTransactions - accountedTransactions,
      },
    },
    {
      label: i18n.t('finance.overview.reconciliation.reconciled'),
      value: {
        completed: reconciledTransactions,
        pending: accountedTransactions - reconciledTransactions,
      },
    },
  ]

  const barLabels = {
    completed: {
      label: i18n.t('finance.overview.reconciliation.completed'),
    },
    pending: {
      label: i18n.t('finance.overview.reconciliation.pending'),
    },
  }

  return {
    countersChartDatas: countersChartDatas,
    barLabels: barLabels,
    chartDatas: chartDatas,
    displayedFilters: prepareDataArgs.displayedFilters,
  }
}

export const buildReconciliationWidget = ({
  queryArgs,
  prepareDataArgs,
}: {
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
}) => {
  const ReconciliationWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas, barLabels, displayedFilters, chartDatas } = data

    return (
      <FinanceWidget
        id="reconciliationDashboard"
        title={i18n.t('finance.overview.reconciliation.title')}
        displayedFilters={displayedFilters}
        countersChartDatas={countersChartDatas}
        type={SupportedCharts.BAR_CHART}
        showChart
        barChartConfig={{
          displayAxis: false,
          barLabels: barLabels,
        }}
        chartDatas={chartDatas}
        emptyState={{
          icon: '🏦',
          text: i18n.t('finance.overview.reconciliation.empty_state.title'),
          description: i18n.t('finance.overview.reconciliation.empty_state.description'),
        }}
      ></FinanceWidget>
    )
  }

  return buildWidget({
    id: 'reconciliationWidget',
    component: ReconciliationWidget,
    document: ReconciliationWidgetDocument,
    prepareData: (data: ReconciliationWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: queryArgs,
  })
}
